import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card, PageHeader, Btn, Pill } from "../components/UI";
import {
  Gauge, Loader2, Send, Sparkles, AlertTriangle, TrendingUp, CheckCircle2,
  RefreshCcw, Tv, Activity,
} from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "How are my campaigns pacing against budget?",
  "Is my creative getting stale anywhere?",
  "Where are my CTV reach gaps?",
  "What should I change this week to improve results?",
];

const FLAG_STYLE = {
  ok: { icon: CheckCircle2, cls: "border-emerald-400/25 bg-emerald-400/5 text-emerald-200" },
  warn: { icon: AlertTriangle, cls: "border-amber-400/25 bg-amber-400/5 text-amber-200" },
  alert: { icon: AlertTriangle, cls: "border-rose-400/30 bg-rose-400/5 text-rose-200" },
};

const renderMsg = (text) => {
  const html = String(text || "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/^#{1,4} (.*)$/gm, "<strong>$1</strong>")
    .replace(/^[-•] /gm, "• ");
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
};

export default function Optimize() {
  const navigate = useNavigate();
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadInsights = async () => {
    setLoadingInsights(true);
    try {
      const { data } = await api.get("/optimize/insights");
      setInsights(data);
    } catch {
      setInsights({ campaigns: [] });
    } finally { setLoadingInsights(false); }
  };
  useEffect(() => { loadInsights(); }, []);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }]);
    try {
      const { data } = await api.post("/optimize/chat", {
        message: msg, session_id: sessionId, campaign_id: campaignFilter || null,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "agent", content: data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The agent hit a snag — try again");
      setMessages((m) => [...m, { role: "agent", content: "Sorry — I hit a snag pulling live data. Try again in a moment." }]);
    } finally { setBusy(false); }
  };

  const campaigns = insights?.campaigns || [];

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="optimize-page">
      <PageHeader
        eyebrow="Measure · Post-Launch"
        title="Optimization Agent"
        subtitle="Live pacing, creative fatigue and reach-gap flags on every launched campaign — and an agent that answers performance questions with real numbers."
        action={<Btn variant="secondary" onClick={loadInsights} disabled={loadingInsights} data-testid="refresh-insights-btn">
          {loadingInsights ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />} Refresh
        </Btn>}
      />

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Insights */}
        <div className="space-y-4 min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
            <Gauge className="w-3.5 h-3.5 text-cyan-300" /> Live campaign health
          </div>
          {loadingInsights ? (
            <Card><div className="py-8 text-center text-sm text-slate-400 flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> Pulling live delivery…</div></Card>
          ) : campaigns.length === 0 ? (
            <Card data-testid="no-live-campaigns">
              <div className="py-8 text-center">
                <Tv className="w-8 h-8 mx-auto text-slate-600 mb-3" />
                <div className="text-sm text-slate-300">No launched campaigns yet</div>
                <p className="text-xs text-slate-500 mt-1">Launch a campaign (Campaign Agent → Approve & Launch) and its live health shows up here.</p>
                <Btn className="mt-4" variant="secondary" onClick={() => navigate("/campaigns/new")}>Create a campaign</Btn>
              </div>
            </Card>
          ) : campaigns.map((c) => (
            <Card key={c.campaign_id} className="!p-4" data-testid={`campaign-health-${c.campaign_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-semibold text-sm truncate">{c.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">${Number(c.budget).toLocaleString()} · {String(c.start_date || "").slice(0, 10)} → {String(c.end_date || "").slice(0, 10)} · {(c.channels || []).join(", ")}</div>
                </div>
                <Pill status={c.status === "Active" ? "Active" : "Pending"}>{c.status}</Pill>
              </div>
              {c.pacing_pct != null && (
                <div className="mt-3">
                  <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    <span>Pacing</span><span className={c.pacing_pct < 80 || c.pacing_pct > 115 ? "text-amber-300" : "text-emerald-300"}>{c.pacing_pct}% of plan</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className={`h-full ${c.pacing_pct < 80 ? "bg-amber-400" : c.pacing_pct > 115 ? "bg-rose-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(c.pacing_pct, 100)}%` }} />
                  </div>
                </div>
              )}
              {c.delivery && (
                <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                  {[["Impr.", c.delivery.impressions.toLocaleString()], ["Households", (c.delivery.unique_households || 0).toLocaleString()],
                    ["Freq.", c.delivery.frequency ? Number(c.delivery.frequency).toFixed(1) : "—"], ["Spend", `$${c.delivery.spend.toLocaleString()}`]].map(([l, v]) => (
                    <div key={l} className="rounded-md bg-white/[0.03] border border-white/5 py-1.5">
                      <div className="text-xs font-mono text-white">{v}</div>
                      <div className="text-[9px] uppercase tracking-wider text-slate-500">{l}</div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 space-y-1.5">
                {(c.flags || []).map((f, i) => {
                  const s = FLAG_STYLE[f.severity] || FLAG_STYLE.warn;
                  return (
                    <div key={i} className={`flex items-start gap-2 text-[11px] rounded-md border px-2.5 py-1.5 ${s.cls}`} data-testid={`flag-${f.type}`}>
                      <s.icon className="w-3.5 h-3.5 shrink-0 mt-px" /> {f.text}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>

        {/* Chat */}
        <Card className="flex flex-col !p-0 min-h-[560px]" padded={false} data-testid="optimize-chat-card">
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-3">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 text-cyan-300" /> Ask about performance
            </div>
            <select value={campaignFilter} onChange={(e) => setCampaignFilter(e.target.value)} data-testid="optimize-campaign-select"
              className="bg-slate-900/80 border border-white/15 rounded-md px-2 py-1.5 text-xs text-slate-300 focus:outline-none focus:border-cyan-400/60 max-w-[220px]">
              <option value="">All launched campaigns</option>
              {campaigns.map((c) => <option key={c.campaign_id} value={c.campaign_id}>{c.name}</option>)}
            </select>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="optimize-messages">
            {messages.length === 0 && !busy && (
              <div>
                <div className="text-sm text-slate-400">I read live delivery for every launched campaign — pacing, frequency, reach, completions and spend. Ask me anything.</div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="optimize-suggestion"
                      className="text-[11px] px-3 py-1.5 rounded-full border border-white/10 text-slate-400 hover:border-cyan-400/30 hover:text-cyan-300 transition-colors text-left">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "bg-cyan-400/15 border border-cyan-400/20 text-cyan-50" : "bg-white/[0.04] border border-white/10 text-slate-200"}`}
                  data-testid={`optimize-msg-${m.role}-${i}`}>
                  {renderMsg(m.content)}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl px-4 py-2.5 text-xs bg-white/[0.04] border border-white/10 text-slate-400 inline-flex items-center gap-2" data-testid="optimize-thinking">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-300" /> Pulling live delivery and analyzing…
                </div>
              </div>
            )}
          </div>
          <div className="p-3 border-t border-white/10">
            <div className="flex gap-2">
              <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="e.g. Why is my CTV campaign underpacing?"
                data-testid="optimize-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-cyan-400/50 placeholder:text-slate-600" />
              <Btn onClick={() => send()} disabled={busy || !input.trim()} data-testid="optimize-send-btn" className="self-end">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Btn>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
